<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Typing_and_Validating/Validator';
    let nextLink= '/Documentation/Interpreter_Framework/The_Expressions_DSL';
    
    import Note from "$lib/notes/Note.svelte";
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="interpreter-framework-1"
  bind:intersecting={visibleById["interpreter-framework-1"]}
  onIntersectChange={(visible) => setVisible("interpreter-framework-1", visible)}
>Interpreter Framework</SectionComponent>
<p>Freon includes an interpreter framework that makes it easy to build an interpreter for your language.</p>
<Note> 
{#snippet header()}The Interpreter Framework is still in Development{/snippet}
{#snippet content()}
<p>The use of an interpreter in the Freon editor is still in development. In particular, 
the way results are presented may still change. Despite this, the current framework is already usable.</p>
{/snippet}
</Note>
<SectionComponent tag="h2" 
  id="what-is-an-interpreter-2"
  bind:intersecting={visibleById["what-is-an-interpreter-2"]}
  onIntersectChange={(visible) => setVisible("what-is-an-interpreter-2", visible)}
>What Is an Interpreter</SectionComponent>
<p>Most articles on interpreters start by describing lexers and parsers, and finish with evaluation.
Forget about lexers and parsers — those are only needed when working directly with plain text.
Since Freon uses a projectional editor, the AST of your model is already available — there’s no
need to parse text to obtain it.</p>
<p>The interpreter in Freon helps to calculate something based on the AST of the model.
The general way to build an interpreter is as follows:</p>
<pre class="language-freon"><code class="language-freon">Select the start node you want to evaluate
   If the node has children/parts
      Evaluate the parts 
      Evaluate the node, using the evaluated value of the parts
   else if the node is a leaf
      Evaluate the node</code></pre>
<p>Take the following AST as an example.</p>
<Figure
imageName={'interpreter/plus-tree.png'}
caption={'AST for a Plus Expression'}
figureNumber={1}
/>
<p>Let’s evaluate the multiply (*) node.</p>
<ul>
<li>We need to first evaluate the two children (+ and 5)
<ul>
<li>Evaluate the plus (+) node, we need to evaluate the children first.
<ul>
<li>Evaluate the “3” node, resulting in the value 3</li>
<li>Evaluate the “4” node, resulting in the value 4</li>
</ul>
</li>
<li>Evaluate the plus node using the results of its children — the result is 7.</li>
<li>Evaluate the “5” node resulting in 5.</li>
</ul>
</li>
<li>Evaluate multiply of 7 and 5, result is 35.</li>
</ul>


<PrevNextSection {prevLink} {nextLink} />